/**
 * Knowledge base categories list with drag-and-drop reordering.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Empty, Modal, Spin, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useKBCategories, useKBArticles, useUpdateCategoryOrder, useDeleteCategory } from '../hooks/useKnowledgeBase';
import { SortableCategoryRow, SortableKbList } from '../components/knowledgeBase';
import type { KBCategory } from '@municipality/types';

export const KnowledgeBaseCategories: React.FC = () => {
  const { t } = useTranslation(['knowledgeBase', 'common']);
  const navigate = useNavigate();
  const [orderedIds, setOrderedIds] = useState<string[]>([]);

  const { data: categories, isLoading } = useKBCategories();
  const { data: articles } = useKBArticles();
  const updateOrderMutation = useUpdateCategoryOrder();
  const deleteMutation = useDeleteCategory();

  const articleCountByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    articles?.forEach((a) => {
      counts[a.categoryId] = (counts[a.categoryId] ?? 0) + 1;
    });
    return counts;
  }, [articles]);

  const displayOrder = useMemo(() => {
    if (orderedIds.length > 0) return orderedIds;
    return categories?.map((c) => c.id) ?? [];
  }, [categories, orderedIds]);

  const handleReorder = useCallback(
    async (newOrder: string[]) => {
      setOrderedIds(newOrder);
      try {
        await updateOrderMutation.mutateAsync(newOrder);
        message.success(t('knowledgeBase:messages.orderUpdated'));
      } catch {
        message.error(t('knowledgeBase:errors.saveError'));
        setOrderedIds([]);
      }
    },
    [updateOrderMutation, t]
  );

  const handleEdit = useCallback(
    (category: { id: string }) => {
      navigate(`/knowledge-base/categories/edit/${category.id}`);
    },
    [navigate]
  );

  const handleDelete = useCallback(
    (category: KBCategory) => {
      const name =
        category.translations?.[category.languageSettings?.primaryLanguage ?? 'en']?.name ??
        category.translations?.en?.name ??
        category.id;
      Modal.confirm({
        title: t('knowledgeBase:messages.confirmDelete'),
        content: t('knowledgeBase:categories.deleteConfirm', { name }),
        okText: t('common:buttons.delete'),
        okType: 'danger',
        cancelText: t('common:buttons.cancel'),
        onOk: async () => {
          try {
            await deleteMutation.mutateAsync(category.id);
            message.success(t('knowledgeBase:messages.categoryDeleted'));
          } catch {
            message.error(t('knowledgeBase:errors.deleteError'));
          }
        },
      });
    },
    [deleteMutation, t]
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>{t('knowledgeBase:categories.title')}</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/knowledge-base/categories/create')}
        >
          {t('knowledgeBase:categories.create')}
        </Button>
      </div>

      <Card>
        {!categories?.length ? (
          <Empty
            description={t('knowledgeBase:categories.noCategories')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/knowledge-base/categories/create')}
            >
              {t('knowledgeBase:categories.create')}
            </Button>
          </Empty>
        ) : (
          <SortableKbList itemIds={displayOrder} onReorder={handleReorder}>
            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              {displayOrder.map((categoryId) => {
                const category = categories?.find((c) => c.id === categoryId);
                if (!category) return null;
                return (
                  <SortableCategoryRow
                    key={category.id}
                    category={category}
                    articleCount={articleCountByCategory[category.id] ?? 0}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                );
              })}
            </div>
          </SortableKbList>
        )}
      </Card>
    </div>
  );
};
